/**
 * Chronicle Registry — single source of truth for per-chronicle dispatch.
 *
 * Architecture (2026-05): the original eager-import registry shipped all
 * 19 chronicles' engines + chapters + news + maps + relationships +
 * entity-configs in the main bundle, producing a 1.2 MB index chunk and
 * a 2.3 MB map chunk. Players paid the bandwidth bill for 18 chronicles
 * they weren't going to play in the next 30 minutes.
 *
 * This file now exposes:
 *
 *   • `loadChronicle(id): Promise<Entry>`     — async loader; one
 *     dynamic chunk per chronicle (Vite splits on every `import()`)
 *   • `useChronicleEntry(id): Entry`          — Suspense-friendly
 *     accessor for React components; throws the loading promise on
 *     cache miss so the surrounding `<Suspense>` can show a fallback
 *   • `getChronicle(id): Entry`               — synchronous accessor;
 *     throws if `id` hasn't been loaded yet. Tests pre-populate the
 *     cache via `beforeAll(loadChronicle)` then use this freely.
 *   • `loadChronicleCities(id): Promise<City[]>` — lighter loader for
 *     surfaces that need only the cities array (e.g. ProtagonistIntro's
 *     resume preview); skips the engine + map component.
 *   • `CHRONICLE_IDS: string[]`               — eager id list; safe to
 *     import anywhere because it's just keys, no chronicle data.
 *
 * Test-side note: `src/test/chronicleRegistry.ts` (the test bundle —
 * different file) is unchanged; it still eagerly imports for synchronous
 * test-runner access. The lazy boundary is production-only.
 */

import type React from "react";
import type { ChronicleEntityConfig } from "@/engine/narrative/types";
import type { City, Choice, GameState, NewsItem, RelationshipActor, StoryNode } from "@/engine/types";

/** Per-city hover info (danger + description) used by the map's hover
 *  tooltip. Mirrors the `cityInfo` record every chronicle's map.ts exports. */
export type CityHoverInfo = { danger: number; description: string };

// Type-only imports for the per-chronicle character-variant types.
// These are erased at build time, so no runtime cost.
import type { AlbigensianCharacter } from "@/engine/AlbigensianCrusadeGameEngine";
import type { VikingDublinCharacter } from "@/engine/VikingDublinGameEngine";
import type { WoRCharacter } from "@/engine/WarsOfTheRosesGameEngine";

// ── Public types ──────────────────────────────────────────────────────

/** The shared engine-adapter shape every chronicle conforms to. */
export interface ChronicleEngine {
  createInitial: () => GameState;
  getNode: (state: GameState) => StoryNode | undefined;
  getChoices: (state: GameState, node: StoryNode) => Choice[];
  applyChoice: (state: GameState, choice: Choice) => GameState;
  getNews: (state: GameState) => NewsItem[];
  getTurnNews: (state: GameState) => NewsItem | null;
  applyNews: (state: GameState, news: NewsItem) => GameState;
  save: (state: GameState) => void;
  load: () => GameState | null;
  getEndingTitle: (state: GameState) => string;
  getEndingDescription: (state: GameState) => string;
}

/** Map component prop shape — every per-chronicle map matches this.
 *  `currentYear` is optional and only consumed by chronicles with a
 *  "living map" (the pan-European Black Death paints its plague overlay
 *  from it); other maps ignore it. Optional, so existing components that
 *  declare only the first two props remain assignable. */
export type ChronicleMapComponent = React.ComponentType<{
  currentLocationId: string;
  travelHistory: string[];
  currentYear?: number;
}>;

/** A single chronicle's full registry entry. */
export interface ChronicleRegistryEntry {
  id: string;
  buildEngine: (searchParams: URLSearchParams) => ChronicleEngine;
  actors: RelationshipActor[];
  mapCities: City[];
  allNews: NewsItem[];
  entityConfig: ChronicleEntityConfig;
  MapComponent: ChronicleMapComponent;
  accentHsl?: string;
  accentForLocation?: (locationId: string) => string | undefined;
}

// ── Adapter helpers ───────────────────────────────────────────────────

/** Most chronicles have engine functions named with their chronicle
 *  prefix. This wraps such a module into the standard ChronicleEngine. */
function adaptStandard(
  engine: {
    createInitial: () => GameState;
    getCurrentNode: (s: GameState) => StoryNode | undefined;
    getAvailableChoices: (s: GameState, n: StoryNode) => Choice[];
    applyChoice: (s: GameState, c: Choice) => GameState;
    getAvailableNews: (s: GameState) => NewsItem[];
    getTurnNews: (s: GameState) => NewsItem | null;
    applyNewsEffects: (s: GameState, n: NewsItem) => GameState;
    save: (s: GameState) => void;
    load: () => GameState | null;
    getEndingTitle: (s: GameState) => string;
    getEndingDescription: (s: GameState) => string;
  },
): ChronicleEngine {
  return {
    createInitial: engine.createInitial,
    getNode: engine.getCurrentNode,
    getChoices: engine.getAvailableChoices,
    applyChoice: engine.applyChoice,
    getNews: engine.getAvailableNews,
    getTurnNews: engine.getTurnNews,
    applyNews: engine.applyNewsEffects,
    save: engine.save,
    load: engine.load,
    getEndingTitle: engine.getEndingTitle,
    getEndingDescription: engine.getEndingDescription,
  };
}

// ── Per-chronicle async loaders ───────────────────────────────────────
//
// Each loader does six co-located dynamic imports (engine, news, map,
// relationships, entity-config, map component). Vite emits one chunk
// per chronicle. The `Promise.all` parallelises the network/disk fetch
// so the user-facing latency is one round-trip per chronicle, not six.

const entryLoaders: Record<string, () => Promise<ChronicleRegistryEntry>> = {
  "eighty-years-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/GameEngine"),
      import("@/data/eighty-years-war/news"),
      import("@/data/eighty-years-war/map"),
      import("@/data/eighty-years-war/relationships"),
      import("@/data/eighty-years-war/entity-config"),
      import("@/components/game/LowCountriesMap"),
    ]);
    return {
      id: "eighty-years-war",
      buildEngine: () => ({
        createInitial: E.createInitialState,
        getNode: E.getCurrentNode,
        getChoices: E.getAvailableChoices,
        applyChoice: E.applyChoice,
        getNews: E.getAvailableNews,
        getTurnNews: E.getTurnNews,
        applyNews: E.applyNewsEffects,
        save: E.saveGame,
        load: E.loadGame,
        getEndingTitle: E.getEndingTitle,
        getEndingDescription: E.getEndingDescription,
      }),
      actors: R.eightyYearsWarActors,
      mapCities: M.cities,
      allNews: N.newsItems,
      entityConfig: EC.eightyYearsWarEntityConfig,
      MapComponent: MC.default,
      accentForLocation: (locId) => {
        const northernIds = ["amsterdam", "haarlem", "leiden", "delft", "rotterdam", "dordrecht"];
        return northernIds.includes(locId) ? "24 90% 55%" : undefined;
      },
    };
  },

  "dutch-golden-age-voc": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/VocGameEngine"),
      import("@/data/dutch-golden-age/news"),
      import("@/data/dutch-golden-age/map"),
      import("@/data/dutch-golden-age/relationships"),
      import("@/data/dutch-golden-age/entity-config"),
      import("@/components/game/VocMap"),
    ]);
    return {
      id: "dutch-golden-age-voc",
      buildEngine: () => adaptStandard({
        createInitial: E.createVocInitialState,
        getCurrentNode: E.getVocCurrentNode,
        getAvailableChoices: E.getVocAvailableChoices,
        applyChoice: E.applyVocChoice,
        getAvailableNews: E.getVocAvailableNews,
        getTurnNews: E.getVocTurnNews,
        applyNewsEffects: E.applyVocNewsEffects,
        save: E.saveVocGame,
        load: E.loadVocGame,
        getEndingTitle: E.getVocEndingTitle,
        getEndingDescription: E.getVocEndingDescription,
      }),
      actors: R.vocActors,
      mapCities: M.vocCities,
      allNews: N.vocNewsItems,
      entityConfig: EC.vocEntityConfig,
      MapComponent: MC.default,
    };
  },

  "burgundian-netherlands": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/BurgundianGameEngine"),
      import("@/data/burgundian-netherlands/news"),
      import("@/data/burgundian-netherlands/map"),
      import("@/data/burgundian-netherlands/relationships"),
      import("@/data/burgundian-netherlands/entity-config"),
      import("@/components/game/BurgundianMap"),
    ]);
    return {
      id: "burgundian-netherlands",
      buildEngine: () => adaptStandard({
        createInitial: E.createBurgundianInitialState,
        getCurrentNode: E.getBurgundianCurrentNode,
        getAvailableChoices: E.getBurgundianAvailableChoices,
        applyChoice: E.applyBurgundianChoice,
        getAvailableNews: E.getBurgundianAvailableNews,
        getTurnNews: E.getBurgundianTurnNews,
        applyNewsEffects: E.applyBurgundianNewsEffects,
        save: E.saveBurgundianGame,
        load: E.loadBurgundianGame,
        getEndingTitle: E.getBurgundianEndingTitle,
        getEndingDescription: E.getBurgundianEndingDescription,
      }),
      actors: R.burgundianActors,
      mapCities: M.burgundianCities,
      allNews: N.burgundianNewsItems,
      entityConfig: EC.burgundianEntityConfig,
      MapComponent: MC.default,
    };
  },

  "belgian-revolution": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/BelgianRevolutionGameEngine"),
      import("@/data/belgian-revolution/news"),
      import("@/data/belgian-revolution/map"),
      import("@/data/belgian-revolution/relationships"),
      import("@/data/belgian-revolution/entity-config"),
      import("@/components/game/BelgianRevolutionMap"),
    ]);
    return {
      id: "belgian-revolution",
      buildEngine: () => adaptStandard({
        createInitial: E.createBelgianRevolutionInitialState,
        getCurrentNode: E.getBelgianRevolutionCurrentNode,
        getAvailableChoices: E.getBelgianRevolutionAvailableChoices,
        applyChoice: E.applyBelgianRevolutionChoice,
        getAvailableNews: E.getBelgianRevolutionAvailableNews,
        getTurnNews: E.getBelgianRevolutionTurnNews,
        applyNewsEffects: E.applyBelgianRevolutionNewsEffects,
        save: E.saveBelgianRevolutionGame,
        load: E.loadBelgianRevolutionGame,
        getEndingTitle: E.getBelgianRevolutionEndingTitle,
        getEndingDescription: E.getBelgianRevolutionEndingDescription,
      }),
      actors: R.belgianRevolutionActors,
      mapCities: M.belgianRevolutionCities,
      allNews: N.belgianRevolutionNewsItems,
      entityConfig: EC.belgianRevolutionEntityConfig,
      MapComponent: MC.default,
    };
  },

  "christianization-scandinavia": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ChristianizationScandinaviaGameEngine"),
      import("@/data/christianization-scandinavia/news"),
      import("@/data/christianization-scandinavia/map"),
      import("@/data/christianization-scandinavia/relationships"),
      import("@/data/christianization-scandinavia/entity-config"),
      import("@/components/game/ChristianizationScandinaviaMap"),
    ]);
    return {
      id: "christianization-scandinavia",
      buildEngine: () => adaptStandard({
        createInitial: E.createChristianizationScandinaviaInitialState,
        getCurrentNode: E.getChristianizationScandinaviaCurrentNode,
        getAvailableChoices: E.getChristianizationScandinaviaAvailableChoices,
        applyChoice: E.applyChristianizationScandinaviaChoice,
        getAvailableNews: E.getChristianizationScandinaviaAvailableNews,
        getTurnNews: E.getChristianizationScandinaviaTurnNews,
        applyNewsEffects: E.applyChristianizationScandinaviaNewsEffects,
        save: E.saveChristianizationScandinaviaGame,
        load: E.loadChristianizationScandinaviaGame,
        getEndingTitle: E.getChristianizationScandinaviaEndingTitle,
        getEndingDescription: E.getChristianizationScandinaviaEndingDescription,
      }),
      actors: R.christianizationScandinaviaActors,
      mapCities: M.christianizationScandinaviaCities,
      allNews: N.christianizationScandinaviaNewsItems,
      entityConfig: EC.christianizationScandinaviaEntityConfig,
      MapComponent: MC.default,
    };
  },

  "settlement-of-iceland": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/SettlementOfIcelandGameEngine"),
      import("@/data/settlement-of-iceland/news"),
      import("@/data/settlement-of-iceland/map"),
      import("@/data/settlement-of-iceland/relationships"),
      import("@/data/settlement-of-iceland/entity-config"),
      import("@/components/game/SettlementOfIcelandMap"),
    ]);
    return {
      id: "settlement-of-iceland",
      buildEngine: () => adaptStandard({
        createInitial: E.createSettlementOfIcelandInitialState,
        getCurrentNode: E.getSettlementOfIcelandCurrentNode,
        getAvailableChoices: E.getSettlementOfIcelandAvailableChoices,
        applyChoice: E.applySettlementOfIcelandChoice,
        getAvailableNews: E.getSettlementOfIcelandAvailableNews,
        getTurnNews: E.getSettlementOfIcelandTurnNews,
        applyNewsEffects: E.applySettlementOfIcelandNewsEffects,
        save: E.saveSettlementOfIcelandGame,
        load: E.loadSettlementOfIcelandGame,
        getEndingTitle: E.getSettlementOfIcelandEndingTitle,
        getEndingDescription: E.getSettlementOfIcelandEndingDescription,
      }),
      actors: R.settlementOfIcelandActors,
      mapCities: M.settlementOfIcelandCities,
      allNews: N.settlementOfIcelandNewsItems,
      entityConfig: EC.settlementOfIcelandEntityConfig,
      MapComponent: MC.default,
    };
  },

  "kalmar-union": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/KalmarUnionGameEngine"),
      import("@/data/kalmar-union/news"),
      import("@/data/kalmar-union/map"),
      import("@/data/kalmar-union/relationships"),
      import("@/data/kalmar-union/entity-config"),
      import("@/components/game/KalmarUnionMap"),
    ]);
    return {
      id: "kalmar-union",
      buildEngine: (params) => {
        const origin = params.get("origin") as "denmark" | "norway" | "sweden" | null;
        return adaptStandard({
          createInitial: () => E.createKalmarUnionInitialState(origin || undefined),
          getCurrentNode: E.getKalmarUnionCurrentNode,
          getAvailableChoices: E.getKalmarUnionAvailableChoices,
          applyChoice: E.applyKalmarUnionChoice,
          getAvailableNews: E.getKalmarUnionAvailableNews,
          getTurnNews: E.getKalmarUnionTurnNews,
          applyNewsEffects: E.applyKalmarUnionNewsEffects,
          save: E.saveKalmarUnionGame,
          load: E.loadKalmarUnionGame,
          getEndingTitle: E.getKalmarUnionEndingTitle,
          getEndingDescription: E.getKalmarUnionEndingDescription,
        });
      },
      actors: R.kalmarUnionActors,
      mapCities: M.kalmarUnionCities,
      allNews: N.kalmarUnionNewsItems,
      entityConfig: EC.kalmarUnionEntityConfig,
      MapComponent: MC.default,
    };
  },

  "great-northern-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/GreatNorthernWarGameEngine"),
      import("@/data/great-northern-war/news"),
      import("@/data/great-northern-war/map"),
      import("@/data/great-northern-war/relationships"),
      import("@/data/great-northern-war/entity-config"),
      import("@/components/game/GreatNorthernWarMap"),
    ]);
    return {
      id: "great-northern-war",
      buildEngine: (params) => {
        const origin = params.get("origin") as "karoliner" | "russian" | null;
        return adaptStandard({
          createInitial: () => E.createGreatNorthernWarInitialState(origin || undefined),
          getCurrentNode: E.getGreatNorthernWarCurrentNode,
          getAvailableChoices: E.getGreatNorthernWarAvailableChoices,
          applyChoice: E.applyGreatNorthernWarChoice,
          getAvailableNews: E.getGreatNorthernWarAvailableNews,
          getTurnNews: E.getGreatNorthernWarTurnNews,
          applyNewsEffects: E.applyGreatNorthernWarNewsEffects,
          save: E.saveGreatNorthernWarGame,
          load: E.loadGreatNorthernWarGame,
          getEndingTitle: E.getGreatNorthernWarEndingTitle,
          getEndingDescription: E.getGreatNorthernWarEndingDescription,
        });
      },
      actors: R.greatNorthernWarActors,
      mapCities: M.greatNorthernWarCities,
      allNews: N.greatNorthernWarNewsItems,
      entityConfig: EC.greatNorthernWarEntityConfig,
      MapComponent: MC.default,
    };
  },

  "world-war-one": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/WorldWarIGameEngine"),
      import("@/data/world-war-one/news"),
      import("@/data/world-war-one/map"),
      import("@/data/world-war-one/relationships"),
      import("@/data/world-war-one/entity-config"),
      import("@/components/game/WorldWarIMap"),
    ]);
    return {
      id: "world-war-one",
      buildEngine: (params) => {
        const origin = params.get("origin") as "poilu" | "prussian" | null;
        return adaptStandard({
          createInitial: () => E.createWorldWarIInitialState(origin || undefined),
          getCurrentNode: E.getWorldWarICurrentNode,
          getAvailableChoices: E.getWorldWarIAvailableChoices,
          applyChoice: E.applyWorldWarIChoice,
          getAvailableNews: E.getWorldWarIAvailableNews,
          getTurnNews: E.getWorldWarITurnNews,
          applyNewsEffects: E.applyWorldWarINewsEffects,
          save: E.saveWorldWarIGame,
          load: E.loadWorldWarIGame,
          getEndingTitle: E.getWorldWarIEndingTitle,
          getEndingDescription: E.getWorldWarIEndingDescription,
        });
      },
      actors: R.worldWarIActors,
      mapCities: M.worldWarICities,
      allNews: N.worldWarINewsItems,
      entityConfig: EC.worldWarIEntityConfig,
      MapComponent: MC.default,
    };
  },

  "livonian-crusade": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/LivonianCrusadeGameEngine"),
      import("@/data/livonian-crusade/news"),
      import("@/data/livonian-crusade/map"),
      import("@/data/livonian-crusade/relationships"),
      import("@/data/livonian-crusade/entity-config"),
      import("@/components/game/LivonianCrusadeMap"),
    ]);
    return {
      id: "livonian-crusade",
      buildEngine: () => adaptStandard({
        createInitial: E.createLivonianCrusadeInitialState,
        getCurrentNode: E.getLivonianCrusadeCurrentNode,
        getAvailableChoices: E.getLivonianCrusadeAvailableChoices,
        applyChoice: E.applyLivonianCrusadeChoice,
        getAvailableNews: E.getLivonianCrusadeAvailableNews,
        getTurnNews: E.getLivonianCrusadeTurnNews,
        applyNewsEffects: E.applyLivonianCrusadeNewsEffects,
        save: E.saveLivonianCrusadeGame,
        load: E.loadLivonianCrusadeGame,
        getEndingTitle: E.getLivonianCrusadeEndingTitle,
        getEndingDescription: E.getLivonianCrusadeEndingDescription,
      }),
      actors: R.livonianCrusadeActors,
      mapCities: M.livonianCrusadeCities,
      allNews: N.livonianCrusadeNewsItems,
      entityConfig: EC.livonianCrusadeEntityConfig,
      MapComponent: MC.default,
    };
  },

  "mongol-sack-of-rus": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/MongolInvasionsGameEngine"),
      import("@/data/mongol-sack-of-rus/news"),
      import("@/data/mongol-sack-of-rus/map"),
      import("@/data/mongol-sack-of-rus/relationships"),
      import("@/data/mongol-sack-of-rus/entity-config"),
      import("@/components/game/MongolInvasionsMap"),
    ]);
    return {
      id: "mongol-sack-of-rus",
      buildEngine: () => adaptStandard({
        createInitial: E.createMongolInvasionsInitialState,
        getCurrentNode: E.getMongolInvasionsCurrentNode,
        getAvailableChoices: E.getMongolInvasionsAvailableChoices,
        applyChoice: E.applyMongolInvasionsChoice,
        getAvailableNews: E.getMongolInvasionsAvailableNews,
        getTurnNews: E.getMongolInvasionsTurnNews,
        applyNewsEffects: E.applyMongolInvasionsNewsEffects,
        save: E.saveMongolInvasionsGame,
        load: E.loadMongolInvasionsGame,
        getEndingTitle: E.getMongolInvasionsEndingTitle,
        getEndingDescription: E.getMongolInvasionsEndingDescription,
      }),
      actors: R.mongolInvasionsActors,
      mapCities: M.mongolInvasionsCities,
      allNews: N.mongolInvasionsNewsItems,
      entityConfig: EC.mongolInvasionsEntityConfig,
      MapComponent: MC.default,
    };
  },

  "the-deluge": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/TheDelugeGameEngine"),
      import("@/data/the-deluge/news"),
      import("@/data/the-deluge/map"),
      import("@/data/the-deluge/relationships"),
      import("@/data/the-deluge/entity-config"),
      import("@/components/game/TheDelugeMap"),
    ]);
    return {
      id: "the-deluge",
      buildEngine: () => adaptStandard({
        createInitial: E.createTheDelugeInitialState,
        getCurrentNode: E.getTheDelugeCurrentNode,
        getAvailableChoices: E.getTheDelugeAvailableChoices,
        applyChoice: E.applyTheDelugeChoice,
        getAvailableNews: E.getTheDelugeAvailableNews,
        getTurnNews: E.getTheDelugeTurnNews,
        applyNewsEffects: E.applyTheDelugeNewsEffects,
        save: E.saveTheDelugeGame,
        load: E.loadTheDelugeGame,
        getEndingTitle: E.getTheDelugeEndingTitle,
        getEndingDescription: E.getTheDelugeEndingDescription,
      }),
      actors: R.delugeActors,
      mapCities: M.delugeCities,
      allNews: N.delugeNewsItems,
      entityConfig: EC.delugeEntityConfig,
      MapComponent: MC.default,
    };
  },

  "reconquista-expansion": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ReconquistaGameEngine"),
      import("@/data/reconquista/news"),
      import("@/data/reconquista/map"),
      import("@/data/reconquista/relationships"),
      import("@/data/reconquista/entity-config"),
      import("@/components/game/ReconquistaMap"),
    ]);
    return {
      id: "reconquista-expansion",
      buildEngine: (params) => {
        const origin = params.get("origin") as "mozarab" | "muslim" | "settler" | null;
        return adaptStandard({
          createInitial: () => E.createReconquistaInitialState(origin || undefined),
          getCurrentNode: E.getReconquistaCurrentNode,
          getAvailableChoices: E.getReconquistaAvailableChoices,
          applyChoice: E.applyReconquistaChoice,
          getAvailableNews: E.getReconquistaAvailableNews,
          getTurnNews: E.getReconquistaTurnNews,
          applyNewsEffects: E.applyReconquistaNewsEffects,
          save: E.saveReconquistaGame,
          load: E.loadReconquistaGame,
          getEndingTitle: E.getReconquistaEndingTitle,
          getEndingDescription: E.getReconquistaEndingDescription,
        });
      },
      actors: R.reconquistaActors,
      mapCities: M.reconquistaCities,
      allNews: N.reconquistaNewsItems,
      entityConfig: EC.reconquistaEntityConfig,
      MapComponent: MC.default,
      accentForLocation: (locId) =>
        ["andalusia", "nasrid", "extremadura", "mountains", "caribbean", "atlantic"].includes(locId)
          ? "160 40% 50%"
          : "0 50% 50%",
    };
  },

  "fall-of-granada": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/FallOfGranadaGameEngine"),
      import("@/data/fall-of-granada/news"),
      import("@/data/fall-of-granada/map"),
      import("@/data/fall-of-granada/relationships"),
      import("@/data/fall-of-granada/entity-config"),
      import("@/components/game/FallOfGranadaMap"),
    ]);
    return {
      id: "fall-of-granada",
      buildEngine: () => adaptStandard({
        createInitial: E.createFallOfGranadaInitialState,
        getCurrentNode: E.getFallOfGranadaCurrentNode,
        getAvailableChoices: E.getFallOfGranadaAvailableChoices,
        applyChoice: E.applyFallOfGranadaChoice,
        getAvailableNews: E.getFallOfGranadaAvailableNews,
        getTurnNews: E.getFallOfGranadaTurnNews,
        applyNewsEffects: E.applyFallOfGranadaNewsEffects,
        save: E.saveFallOfGranadaGame,
        load: E.loadFallOfGranadaGame,
        getEndingTitle: E.getFallOfGranadaEndingTitle,
        getEndingDescription: E.getFallOfGranadaEndingDescription,
      }),
      actors: R.fallOfGranadaActors,
      mapCities: M.fallOfGranadaCities,
      allNews: N.fallOfGranadaNewsItems,
      entityConfig: EC.fallOfGranadaEntityConfig,
      MapComponent: MC.default,
      accentForLocation: (locId) =>
        ["andalusia", "nasrid", "extremadura", "mountains", "caribbean", "atlantic"].includes(locId)
          ? "160 40% 50%"
          : "0 50% 50%",
    };
  },

  "first-voyages-west": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/FirstVoyagesWestGameEngine"),
      import("@/data/first-voyages-west/news"),
      import("@/data/first-voyages-west/map"),
      import("@/data/first-voyages-west/relationships"),
      import("@/data/first-voyages-west/entity-config"),
      import("@/components/game/FirstVoyagesWestMap"),
    ]);
    return {
      id: "first-voyages-west",
      buildEngine: () => adaptStandard({
        createInitial: E.createFirstVoyagesWestInitialState,
        getCurrentNode: E.getFirstVoyagesWestCurrentNode,
        getAvailableChoices: E.getFirstVoyagesWestAvailableChoices,
        applyChoice: E.applyFirstVoyagesWestChoice,
        getAvailableNews: E.getFirstVoyagesWestAvailableNews,
        getTurnNews: E.getFirstVoyagesWestTurnNews,
        applyNewsEffects: E.applyFirstVoyagesWestNewsEffects,
        save: E.saveFirstVoyagesWestGame,
        load: E.loadFirstVoyagesWestGame,
        getEndingTitle: E.getFirstVoyagesWestEndingTitle,
        getEndingDescription: E.getFirstVoyagesWestEndingDescription,
      }),
      actors: R.firstVoyagesWestActors,
      mapCities: M.firstVoyagesWestCities,
      allNews: N.firstVoyagesWestNewsItems,
      entityConfig: EC.firstVoyagesWestEntityConfig,
      MapComponent: MC.default,
      accentHsl: "200 50% 50%",
    };
  },

  "iberian-union": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/IberianUnionGameEngine"),
      import("@/data/iberian-union/news"),
      import("@/data/iberian-union/map"),
      import("@/data/iberian-union/relationships"),
      import("@/data/iberian-union/entity-config"),
      import("@/components/game/IberianUnionMap"),
    ]);
    return {
      id: "iberian-union",
      buildEngine: () => adaptStandard({
        createInitial: E.createIberianUnionInitialState,
        getCurrentNode: E.getIberianUnionCurrentNode,
        getAvailableChoices: E.getIberianUnionAvailableChoices,
        applyChoice: E.applyIberianUnionChoice,
        getAvailableNews: E.getIberianUnionAvailableNews,
        getTurnNews: E.getIberianUnionTurnNews,
        applyNewsEffects: E.applyIberianUnionNewsEffects,
        save: E.saveIberianUnionGame,
        load: E.loadIberianUnionGame,
        getEndingTitle: E.getIberianUnionEndingTitle,
        getEndingDescription: E.getIberianUnionEndingDescription,
      }),
      actors: R.iberianUnionActors,
      mapCities: M.iberianUnionCities,
      allNews: N.iberianUnionNewsItems,
      entityConfig: EC.iberianUnionEntityConfig,
      MapComponent: MC.default,
      accentHsl: "200 40% 45%",
    };
  },

  "peninsular-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/PeninsularWarGameEngine"),
      import("@/data/peninsular-war/news"),
      import("@/data/peninsular-war/map"),
      import("@/data/peninsular-war/relationships"),
      import("@/data/peninsular-war/entity-config"),
      import("@/components/game/PeninsularWarMap"),
    ]);
    return {
      id: "peninsular-war",
      buildEngine: () => adaptStandard({
        createInitial: E.createPeninsularWarInitialState,
        getCurrentNode: E.getPeninsularWarCurrentNode,
        getAvailableChoices: E.getPeninsularWarAvailableChoices,
        applyChoice: E.applyPeninsularWarChoice,
        getAvailableNews: E.getPeninsularWarAvailableNews,
        getTurnNews: E.getPeninsularWarTurnNews,
        applyNewsEffects: E.applyPeninsularWarNewsEffects,
        save: E.savePeninsularWarGame,
        load: E.loadPeninsularWarGame,
        getEndingTitle: E.getPeninsularWarEndingTitle,
        getEndingDescription: E.getPeninsularWarEndingDescription,
      }),
      actors: R.peninsularWarActors,
      mapCities: M.peninsularWarCities,
      allNews: N.peninsularWarNewsItems,
      entityConfig: EC.peninsularWarEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 50% 50%",
    };
  },

  "spanish-civil-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/SpanishCivilWarGameEngine"),
      import("@/data/spanish-civil-war/news"),
      import("@/data/spanish-civil-war/map"),
      import("@/data/spanish-civil-war/relationships"),
      import("@/data/spanish-civil-war/entity-config"),
      import("@/components/game/SpanishCivilWarMap"),
    ]);
    return {
      id: "spanish-civil-war",
      buildEngine: () => adaptStandard({
        createInitial: E.createSpanishCivilWarInitialState,
        getCurrentNode: E.getSpanishCivilWarCurrentNode,
        getAvailableChoices: E.getSpanishCivilWarAvailableChoices,
        applyChoice: E.applySpanishCivilWarChoice,
        getAvailableNews: E.getSpanishCivilWarAvailableNews,
        getTurnNews: E.getSpanishCivilWarTurnNews,
        applyNewsEffects: E.applySpanishCivilWarNewsEffects,
        save: E.saveSpanishCivilWarGame,
        load: E.loadSpanishCivilWarGame,
        getEndingTitle: E.getSpanishCivilWarEndingTitle,
        getEndingDescription: E.getSpanishCivilWarEndingDescription,
      }),
      actors: R.spanishCivilWarActors,
      mapCities: M.spanishCivilWarCities,
      allNews: N.spanishCivilWarNewsItems,
      entityConfig: EC.spanishCivilWarEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 50% 50%",
    };
  },

  "hundred-years-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/HundredYearsWarGameEngine"),
      import("@/data/hundred-years-war/news"),
      import("@/data/hundred-years-war/map"),
      import("@/data/hundred-years-war/relationships"),
      import("@/data/hundred-years-war/entity-config"),
      import("@/components/game/HundredYearsWarMap"),
    ]);
    return {
      id: "hundred-years-war",
      buildEngine: () => adaptStandard({
        createInitial: E.createHundredYearsWarInitialState,
        getCurrentNode: E.getHundredYearsWarCurrentNode,
        getAvailableChoices: E.getHundredYearsWarAvailableChoices,
        applyChoice: E.applyHundredYearsWarChoice,
        getAvailableNews: E.getHundredYearsWarAvailableNews,
        getTurnNews: E.getHundredYearsWarTurnNews,
        applyNewsEffects: E.applyHundredYearsWarNewsEffects,
        save: E.saveHundredYearsWarGame,
        load: E.loadHundredYearsWarGame,
        getEndingTitle: E.getHundredYearsWarEndingTitle,
        getEndingDescription: E.getHundredYearsWarEndingDescription,
      }),
      actors: R.hundredYearsWarActors,
      mapCities: M.hundredYearsWarCities,
      allNews: N.hundredYearsWarNewsItems,
      entityConfig: EC.hundredYearsWarEntityConfig,
      MapComponent: MC.default,
      accentHsl: "220 45% 50%",
    };
  },

  "french-revolution": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/FrenchRevolutionGameEngine"),
      import("@/data/french-revolution/news"),
      import("@/data/french-revolution/map"),
      import("@/data/french-revolution/relationships"),
      import("@/data/french-revolution/entity-config"),
      import("@/components/game/FrenchRevolutionMap"),
    ]);
    return {
      id: "french-revolution",
      buildEngine: () => adaptStandard({
        createInitial: E.createFrenchRevolutionInitialState,
        getCurrentNode: E.getFrenchRevolutionCurrentNode,
        getAvailableChoices: E.getFrenchRevolutionAvailableChoices,
        applyChoice: E.applyFrenchRevolutionChoice,
        getAvailableNews: E.getFrenchRevolutionAvailableNews,
        getTurnNews: E.getFrenchRevolutionTurnNews,
        applyNewsEffects: E.applyFrenchRevolutionNewsEffects,
        save: E.saveFrenchRevolutionGame,
        load: E.loadFrenchRevolutionGame,
        getEndingTitle: E.getFrenchRevolutionEndingTitle,
        getEndingDescription: E.getFrenchRevolutionEndingDescription,
      }),
      actors: R.frenchRevolutionActors,
      mapCities: M.frenchRevolutionCities,
      allNews: N.frenchRevolutionNewsItems,
      entityConfig: EC.frenchRevolutionEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 55% 50%",
    };
  },

  "second-republic": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/SecondRepublicGameEngine"),
      import("@/data/second-republic/news"),
      import("@/data/second-republic/map"),
      import("@/data/second-republic/relationships"),
      import("@/data/second-republic/entity-config"),
      import("@/components/game/SecondRepublicMap"),
    ]);
    return {
      id: "second-republic",
      buildEngine: () => adaptStandard({
        createInitial: E.createSecondRepublicInitialState,
        getCurrentNode: E.getSecondRepublicCurrentNode,
        getAvailableChoices: E.getSecondRepublicAvailableChoices,
        applyChoice: E.applySecondRepublicChoice,
        getAvailableNews: E.getSecondRepublicAvailableNews,
        getTurnNews: E.getSecondRepublicTurnNews,
        applyNewsEffects: E.applySecondRepublicNewsEffects,
        save: E.saveSecondRepublicGame,
        load: E.loadSecondRepublicGame,
        getEndingTitle: E.getSecondRepublicEndingTitle,
        getEndingDescription: E.getSecondRepublicEndingDescription,
      }),
      actors: R.secondRepublicActors,
      mapCities: M.secondRepublicCities,
      allNews: N.secondRepublicNewsItems,
      entityConfig: EC.secondRepublicEntityConfig,
      MapComponent: MC.default,
      // Republican gold, faction-neutral tricolour hue.
      accentHsl: "43 60% 50%",
    };
  },

  "albigensian-crusade": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/AlbigensianCrusadeGameEngine"),
      import("@/data/albigensian-crusade/news"),
      import("@/data/albigensian-crusade/map"),
      import("@/data/albigensian-crusade/relationships"),
      import("@/data/albigensian-crusade/entity-config"),
      import("@/components/game/AlbigensianCrusadeMap"),
    ]);
    return {
      id: "albigensian-crusade",
      buildEngine: (params) => {
        const character = (params.get("character") as AlbigensianCharacter | null) || "bernat";
        return adaptStandard({
          createInitial: () => E.createAlbigensianCrusadeInitialState(character),
          getCurrentNode: E.getAlbigensianCrusadeCurrentNode,
          getAvailableChoices: E.getAlbigensianCrusadeAvailableChoices,
          applyChoice: E.applyAlbigensianCrusadeChoice,
          getAvailableNews: E.getAlbigensianCrusadeAvailableNews,
          getTurnNews: E.getAlbigensianCrusadeTurnNews,
          applyNewsEffects: E.applyAlbigensianCrusadeNewsEffects,
          save: E.saveAlbigensianCrusadeGame,
          load: E.loadAlbigensianCrusadeGame,
          getEndingTitle: E.getAlbigensianCrusadeEndingTitle,
          getEndingDescription: E.getAlbigensianCrusadeEndingDescription,
        });
      },
      actors: R.albigensianCrusadeActors,
      mapCities: M.albigensianCrusadeCities,
      allNews: N.albigensianCrusadeNewsItems,
      entityConfig: EC.albigensianCrusadeEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 55% 48%",
    };
  },

  "viking-dublin": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/VikingDublinGameEngine"),
      import("@/data/viking-dublin/news"),
      import("@/data/viking-dublin/map"),
      import("@/data/viking-dublin/relationships"),
      import("@/data/viking-dublin/entity-config"),
      import("@/components/game/VikingDublinMap"),
    ]);
    return {
      id: "viking-dublin",
      buildEngine: (params) => {
        const character = (params.get("character") as VikingDublinCharacter | null) || "aed";
        return adaptStandard({
          createInitial: () => E.createVikingDublinInitialState(character),
          getCurrentNode: E.getVikingDublinCurrentNode,
          getAvailableChoices: E.getVikingDublinAvailableChoices,
          applyChoice: E.applyVikingDublinChoice,
          getAvailableNews: E.getVikingDublinAvailableNews,
          getTurnNews: E.getVikingDublinTurnNews,
          applyNewsEffects: E.applyVikingDublinNewsEffects,
          save: E.saveVikingDublinGame,
          load: E.loadVikingDublinGame,
          getEndingTitle: E.getVikingDublinEndingTitle,
          getEndingDescription: E.getVikingDublinEndingDescription,
        });
      },
      actors: R.vikingDublinActors,
      mapCities: M.vikingDublinCities,
      allNews: N.vikingDublinNewsItems,
      entityConfig: EC.vikingDublinEntityConfig,
      MapComponent: MC.default,
      accentHsl: "210 50% 48%",
    };
  },

  "norman-conquest": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/NormanConquestGameEngine"),
      import("@/data/norman-conquest/news"),
      import("@/data/norman-conquest/map"),
      import("@/data/norman-conquest/relationships"),
      import("@/data/norman-conquest/entity-config"),
      import("@/components/game/NormanConquestMap"),
    ]);
    return {
      id: "norman-conquest",
      buildEngine: () => adaptStandard({
        createInitial: E.createNormanConquestInitialState,
        getCurrentNode: E.getNormanConquestCurrentNode,
        getAvailableChoices: E.getNormanConquestAvailableChoices,
        applyChoice: E.applyNormanConquestChoice,
        getAvailableNews: E.getNormanConquestAvailableNews,
        getTurnNews: E.getNormanConquestTurnNews,
        applyNewsEffects: E.applyNormanConquestNewsEffects,
        save: E.saveNormanConquestGame,
        load: E.loadNormanConquestGame,
        getEndingTitle: E.getNormanConquestEndingTitle,
        getEndingDescription: E.getNormanConquestEndingDescription,
      }),
      actors: R.normanConquestActors,
      mapCities: M.normanConquestCities,
      allNews: N.normanConquestNewsItems,
      entityConfig: EC.normanConquestEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 55% 48%",
    };
  },

  "the-anarchy": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/TheAnarchyGameEngine"),
      import("@/data/the-anarchy/news"),
      import("@/data/the-anarchy/map"),
      import("@/data/the-anarchy/relationships"),
      import("@/data/the-anarchy/entity-config"),
      import("@/components/game/TheAnarchyMap"),
    ]);
    return {
      id: "the-anarchy",
      buildEngine: () => adaptStandard({
        createInitial: E.createTheAnarchyInitialState,
        getCurrentNode: E.getTheAnarchyCurrentNode,
        getAvailableChoices: E.getTheAnarchyAvailableChoices,
        applyChoice: E.applyTheAnarchyChoice,
        getAvailableNews: E.getTheAnarchyAvailableNews,
        getTurnNews: E.getTheAnarchyTurnNews,
        applyNewsEffects: E.applyTheAnarchyNewsEffects,
        save: E.saveTheAnarchyGame,
        load: E.loadTheAnarchyGame,
        getEndingTitle: E.getTheAnarchyEndingTitle,
        getEndingDescription: E.getTheAnarchyEndingDescription,
      }),
      actors: R.anarchyActors,
      mapCities: M.anarchyCities,
      allNews: N.anarchyNewsItems,
      entityConfig: EC.anarchyEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 55% 48%",
    };
  },

  "wars-of-the-roses": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/WarsOfTheRosesGameEngine"),
      import("@/data/wars-of-the-roses/news"),
      import("@/data/wars-of-the-roses/map"),
      import("@/data/wars-of-the-roses/relationships"),
      import("@/data/wars-of-the-roses/entity-config"),
      import("@/components/game/WarsOfTheRosesMap"),
    ]);
    return {
      id: "wars-of-the-roses",
      buildEngine: (params) => {
        const character = (params.get("character") as WoRCharacter | null) || "margery";
        return adaptStandard({
          createInitial: () => E.createWarsOfTheRosesInitialState(character),
          getCurrentNode: E.getWarsOfTheRosesCurrentNode,
          getAvailableChoices: E.getWarsOfTheRosesAvailableChoices,
          applyChoice: E.applyWarsOfTheRosesChoice,
          getAvailableNews: E.getWarsOfTheRosesAvailableNews,
          getTurnNews: E.getWarsOfTheRosesTurnNews,
          applyNewsEffects: E.applyWarsOfTheRosesNewsEffects,
          save: E.saveWarsOfTheRosesGame,
          load: E.loadWarsOfTheRosesGame,
          getEndingTitle: E.getWarsOfTheRosesEndingTitle,
          getEndingDescription: E.getWarsOfTheRosesEndingDescription,
        });
      },
      actors: R.worsOfTheRosesActors,
      mapCities: M.worsOfTheRosesCities,
      allNews: N.worsOfTheRosesNewsItems,
      entityConfig: EC.worsOfTheRosesEntityConfig,
      MapComponent: MC.default,
      // Faction-neutral gold accent — both POVs share the same map.
      accentHsl: "43 65% 50%",
    };
  },

  "english-civil-wars": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/EnglishCivilWarsGameEngine"),
      import("@/data/english-civil-wars/news"),
      import("@/data/english-civil-wars/map"),
      import("@/data/english-civil-wars/relationships"),
      import("@/data/english-civil-wars/entity-config"),
      import("@/components/game/EnglishCivilWarsMap"),
    ]);
    return {
      id: "english-civil-wars",
      buildEngine: () => adaptStandard({
        createInitial: E.createEnglishCivilWarsInitialState,
        getCurrentNode: E.getEnglishCivilWarsCurrentNode,
        getAvailableChoices: E.getEnglishCivilWarsAvailableChoices,
        applyChoice: E.applyEnglishCivilWarsChoice,
        getAvailableNews: E.getEnglishCivilWarsAvailableNews,
        getTurnNews: E.getEnglishCivilWarsTurnNews,
        applyNewsEffects: E.applyEnglishCivilWarsNewsEffects,
        save: E.saveEnglishCivilWarsGame,
        load: E.loadEnglishCivilWarsGame,
        getEndingTitle: E.getEnglishCivilWarsEndingTitle,
        getEndingDescription: E.getEnglishCivilWarsEndingDescription,
      }),
      actors: R.ecwActors,
      mapCities: M.ecwCities,
      allNews: N.ecwNewsItems,
      entityConfig: EC.ecwEntityConfig,
      MapComponent: MC.default,
      // Genevan blue — Stoneham's Presbyterian-by-training POV.
      accentHsl: "220 55% 55%",
    };
  },

  "irish-independence": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/IrishIndependenceGameEngine"),
      import("@/data/irish-independence/news"),
      import("@/data/irish-independence/map"),
      import("@/data/irish-independence/relationships"),
      import("@/data/irish-independence/entity-config"),
      import("@/components/game/IrishIndependenceMap"),
    ]);
    return {
      id: "irish-independence",
      buildEngine: () => adaptStandard({
        createInitial: E.createIrishIndependenceInitialState,
        getCurrentNode: E.getIrishIndependenceCurrentNode,
        getAvailableChoices: E.getIrishIndependenceAvailableChoices,
        applyChoice: E.applyIrishIndependenceChoice,
        getAvailableNews: E.getIrishIndependenceAvailableNews,
        getTurnNews: E.getIrishIndependenceTurnNews,
        applyNewsEffects: E.applyIrishIndependenceNewsEffects,
        save: E.saveIrishIndependenceGame,
        load: E.loadIrishIndependenceGame,
        getEndingTitle: E.getIrishIndependenceEndingTitle,
        getEndingDescription: E.getIrishIndependenceEndingDescription,
      }),
      actors: R.iwoiActors,
      mapCities: M.iwoiCities,
      allNews: N.iwoiNewsItems,
      entityConfig: EC.iwoiEntityConfig,
      MapComponent: MC.default,
      // Republican green.
      accentHsl: "135 50% 45%",
    };
  },

  "russian-revolution": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/RussianRevolutionGameEngine"),
      import("@/data/russian-revolution/news"),
      import("@/data/russian-revolution/map"),
      import("@/data/russian-revolution/relationships"),
      import("@/data/russian-revolution/entity-config"),
      import("@/components/game/RussianRevolutionMap"),
    ]);
    return {
      id: "russian-revolution",
      buildEngine: (params) => {
        const origin = params.get("origin") as "red" | "white" | null;
        return adaptStandard({
          createInitial: () => E.createRussianRevolutionInitialState(origin || undefined),
          getCurrentNode: E.getRussianRevolutionCurrentNode,
          getAvailableChoices: E.getRussianRevolutionAvailableChoices,
          applyChoice: E.applyRussianRevolutionChoice,
          getAvailableNews: E.getRussianRevolutionAvailableNews,
          getTurnNews: E.getRussianRevolutionTurnNews,
          applyNewsEffects: E.applyRussianRevolutionNewsEffects,
          save: E.saveRussianRevolutionGame,
          load: E.loadRussianRevolutionGame,
          getEndingTitle: E.getRussianRevolutionEndingTitle,
          getEndingDescription: E.getRussianRevolutionEndingDescription,
        });
      },
      actors: R.russianRevolutionActors,
      mapCities: M.russianRevolutionCities,
      allNews: N.russianRevolutionNewsItems,
      entityConfig: EC.russianRevolutionEntityConfig,
      MapComponent: MC.default,
    };
  },

  "hungarian-revolution-1956": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/HungarianRevolution1956GameEngine"),
      import("@/data/hungarian-revolution-1956/news"),
      import("@/data/hungarian-revolution-1956/map"),
      import("@/data/hungarian-revolution-1956/relationships"),
      import("@/data/hungarian-revolution-1956/entity-config"),
      import("@/components/game/HungarianRevolution1956Map"),
    ]);
    return {
      id: "hungarian-revolution-1956",
      buildEngine: () => adaptStandard({
        createInitial: E.createHungarianRevolution1956InitialState,
        getCurrentNode: E.getHungarianRevolution1956CurrentNode,
        getAvailableChoices: E.getHungarianRevolution1956AvailableChoices,
        applyChoice: E.applyHungarianRevolution1956Choice,
        getAvailableNews: E.getHungarianRevolution1956AvailableNews,
        getTurnNews: E.getHungarianRevolution1956TurnNews,
        applyNewsEffects: E.applyHungarianRevolution1956NewsEffects,
        save: E.saveHungarianRevolution1956Game,
        load: E.loadHungarianRevolution1956Game,
        getEndingTitle: E.getHungarianRevolution1956EndingTitle,
        getEndingDescription: E.getHungarianRevolution1956EndingDescription,
      }),
      actors: R.hungarianRevolution1956Actors,
      mapCities: M.hungarianRevolution1956Cities,
      allNews: N.hungarianRevolution1956NewsItems,
      entityConfig: EC.hungarianRevolution1956EntityConfig,
      MapComponent: MC.default,
    };
  },

  "hungarian-revolution-1848": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/HungarianRevolution1848GameEngine"),
      import("@/data/hungarian-revolution-1848/news"),
      import("@/data/hungarian-revolution-1848/map"),
      import("@/data/hungarian-revolution-1848/relationships"),
      import("@/data/hungarian-revolution-1848/entity-config"),
      import("@/components/game/HungarianRevolution1848Map"),
    ]);
    return {
      id: "hungarian-revolution-1848",
      buildEngine: () => adaptStandard({
        createInitial: E.createHungarianRevolution1848InitialState,
        getCurrentNode: E.getHungarianRevolution1848CurrentNode,
        getAvailableChoices: E.getHungarianRevolution1848AvailableChoices,
        applyChoice: E.applyHungarianRevolution1848Choice,
        getAvailableNews: E.getHungarianRevolution1848AvailableNews,
        getTurnNews: E.getHungarianRevolution1848TurnNews,
        applyNewsEffects: E.applyHungarianRevolution1848NewsEffects,
        save: E.saveHungarianRevolution1848Game,
        load: E.loadHungarianRevolution1848Game,
        getEndingTitle: E.getHungarianRevolution1848EndingTitle,
        getEndingDescription: E.getHungarianRevolution1848EndingDescription,
      }),
      actors: R.hungarianRevolution1848Actors,
      mapCities: M.hungarianRevolution1848Cities,
      allNews: N.hungarianRevolution1848NewsItems,
      entityConfig: EC.hungarianRevolution1848EntityConfig,
      MapComponent: MC.default,
      // Hungarian tricolour green — faction-neutral national accent.
      accentHsl: "140 50% 45%",
    };
  },

  "prague-spring-1968": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/PragueSpring1968GameEngine"),
      import("@/data/prague-spring-1968/news"),
      import("@/data/prague-spring-1968/map"),
      import("@/data/prague-spring-1968/relationships"),
      import("@/data/prague-spring-1968/entity-config"),
      import("@/components/game/PragueSpring1968Map"),
    ]);
    return {
      id: "prague-spring-1968",
      buildEngine: () => adaptStandard({
        createInitial: E.createPragueSpring1968InitialState,
        getCurrentNode: E.getPragueSpring1968CurrentNode,
        getAvailableChoices: E.getPragueSpring1968AvailableChoices,
        applyChoice: E.applyPragueSpring1968Choice,
        getAvailableNews: E.getPragueSpring1968AvailableNews,
        getTurnNews: E.getPragueSpring1968TurnNews,
        applyNewsEffects: E.applyPragueSpring1968NewsEffects,
        save: E.savePragueSpring1968Game,
        load: E.loadPragueSpring1968Game,
        getEndingTitle: E.getPragueSpring1968EndingTitle,
        getEndingDescription: E.getPragueSpring1968EndingDescription,
      }),
      actors: R.pragueSpring1968Actors,
      mapCities: M.pragueSpring1968Cities,
      allNews: N.pragueSpring1968NewsItems,
      entityConfig: EC.pragueSpring1968EntityConfig,
      MapComponent: MC.default,
    };
  },

  "two-germanies": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/TwoGermaniesGameEngine"),
      import("@/data/two-germanies/news"),
      import("@/data/two-germanies/map"),
      import("@/data/two-germanies/relationships"),
      import("@/data/two-germanies/entity-config"),
      import("@/components/game/TwoGermaniesMap"),
    ]);
    return {
      id: "two-germanies",
      buildEngine: () => adaptStandard({
        createInitial: E.createTwoGermaniesInitialState,
        getCurrentNode: E.getTwoGermaniesCurrentNode,
        getAvailableChoices: E.getTwoGermaniesAvailableChoices,
        applyChoice: E.applyTwoGermaniesChoice,
        getAvailableNews: E.getTwoGermaniesAvailableNews,
        getTurnNews: E.getTwoGermaniesTurnNews,
        applyNewsEffects: E.applyTwoGermaniesNewsEffects,
        save: E.saveTwoGermaniesGame,
        load: E.loadTwoGermaniesGame,
        getEndingTitle: E.getTwoGermaniesEndingTitle,
        getEndingDescription: E.getTwoGermaniesEndingDescription,
      }),
      actors: R.twoGermaniesActors,
      mapCities: M.twoGermaniesCities,
      allNews: N.twoGermaniesNewsItems,
      entityConfig: EC.twoGermaniesEntityConfig,
      MapComponent: MC.default,
    };
  },

  "fall-of-communism": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/FallOfCommunismGameEngine"),
      import("@/data/fall-of-communism/news"),
      import("@/data/fall-of-communism/map"),
      import("@/data/fall-of-communism/relationships"),
      import("@/data/fall-of-communism/entity-config"),
      import("@/components/game/FallOfCommunismMap"),
    ]);
    return {
      id: "fall-of-communism",
      buildEngine: (params) => {
        const origin = params.get("origin") as "prague" | "warsaw" | "bucharest" | null;
        return adaptStandard({
          createInitial: () => E.createFallOfCommunismInitialState(origin || undefined),
          getCurrentNode: E.getFallOfCommunismCurrentNode,
          getAvailableChoices: E.getFallOfCommunismAvailableChoices,
          applyChoice: E.applyFallOfCommunismChoice,
          getAvailableNews: E.getFallOfCommunismAvailableNews,
          getTurnNews: E.getFallOfCommunismTurnNews,
          applyNewsEffects: E.applyFallOfCommunismNewsEffects,
          save: E.saveFallOfCommunismGame,
          load: E.loadFallOfCommunismGame,
          getEndingTitle: E.getFallOfCommunismEndingTitle,
          getEndingDescription: E.getFallOfCommunismEndingDescription,
        });
      },
      actors: R.fallOfCommunismActors,
      mapCities: M.fallOfCommunismCities,
      allNews: N.fallOfCommunismNewsItems,
      entityConfig: EC.fallOfCommunismEntityConfig,
      MapComponent: MC.default,
    };
  },

  "italian-city-states": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ItalianCityStatesGameEngine"),
      import("@/data/italian-city-states/news"),
      import("@/data/italian-city-states/map"),
      import("@/data/italian-city-states/relationships"),
      import("@/data/italian-city-states/entity-config"),
      import("@/components/game/ItalianCityStatesMap"),
    ]);
    return {
      id: "italian-city-states",
      buildEngine: () => adaptStandard({
        createInitial: E.createItalianCityStatesInitialState,
        getCurrentNode: E.getItalianCityStatesCurrentNode,
        getAvailableChoices: E.getItalianCityStatesAvailableChoices,
        applyChoice: E.applyItalianCityStatesChoice,
        getAvailableNews: E.getItalianCityStatesAvailableNews,
        getTurnNews: E.getItalianCityStatesTurnNews,
        applyNewsEffects: E.applyItalianCityStatesNewsEffects,
        save: E.saveItalianCityStatesGame,
        load: E.loadItalianCityStatesGame,
        getEndingTitle: E.getItalianCityStatesEndingTitle,
        getEndingDescription: E.getItalianCityStatesEndingDescription,
      }),
      actors: R.italianCityStatesActors,
      mapCities: M.italianCityStatesCities,
      allNews: N.italianCityStatesNewsItems,
      entityConfig: EC.italianCityStatesEntityConfig,
      MapComponent: MC.default,
      accentHsl: "43 65% 50%",
    };
  },

  "medici-florence": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/MediciFlorenceGameEngine"),
      import("@/data/medici-florence/news"),
      import("@/data/medici-florence/map"),
      import("@/data/medici-florence/relationships"),
      import("@/data/medici-florence/entity-config"),
      import("@/components/game/MediciFlorenceMap"),
    ]);
    return {
      id: "medici-florence",
      buildEngine: () => adaptStandard({
        createInitial: E.createMediciFlorenceInitialState,
        getCurrentNode: E.getMediciFlorenceCurrentNode,
        getAvailableChoices: E.getMediciFlorenceAvailableChoices,
        applyChoice: E.applyMediciFlorenceChoice,
        getAvailableNews: E.getMediciFlorenceAvailableNews,
        getTurnNews: E.getMediciFlorenceTurnNews,
        applyNewsEffects: E.applyMediciFlorenceNewsEffects,
        save: E.saveMediciFlorenceGame,
        load: E.loadMediciFlorenceGame,
        getEndingTitle: E.getMediciFlorenceEndingTitle,
        getEndingDescription: E.getMediciFlorenceEndingDescription,
      }),
      actors: R.mediciFlorenceActors,
      mapCities: M.mediciFlorenceCities,
      allNews: N.mediciFlorenceNewsItems,
      entityConfig: EC.mediciFlorenceEntityConfig,
      MapComponent: MC.default,
      accentHsl: "40 70% 52%",
    };
  },

  "italian-wars": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ItalianWarsGameEngine"),
      import("@/data/italian-wars/news"),
      import("@/data/italian-wars/map"),
      import("@/data/italian-wars/relationships"),
      import("@/data/italian-wars/entity-config"),
      import("@/components/game/ItalianWarsMap"),
    ]);
    return {
      id: "italian-wars",
      buildEngine: () => adaptStandard({
        createInitial: E.createItalianWarsInitialState,
        getCurrentNode: E.getItalianWarsCurrentNode,
        getAvailableChoices: E.getItalianWarsAvailableChoices,
        applyChoice: E.applyItalianWarsChoice,
        getAvailableNews: E.getItalianWarsAvailableNews,
        getTurnNews: E.getItalianWarsTurnNews,
        applyNewsEffects: E.applyItalianWarsNewsEffects,
        save: E.saveItalianWarsGame,
        load: E.loadItalianWarsGame,
        getEndingTitle: E.getItalianWarsEndingTitle,
        getEndingDescription: E.getItalianWarsEndingDescription,
      }),
      actors: R.italianWarsActors,
      mapCities: M.italianWarsCities,
      allNews: N.italianWarsNewsItems,
      entityConfig: EC.italianWarsEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 65% 52%",
    };
  },

  "italian-unification": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ItalianUnificationGameEngine"),
      import("@/data/italian-unification/news"),
      import("@/data/italian-unification/map"),
      import("@/data/italian-unification/relationships"),
      import("@/data/italian-unification/entity-config"),
      import("@/components/game/ItalianUnificationMap"),
    ]);
    return {
      id: "italian-unification",
      buildEngine: (params) => {
        const origin = params.get("origin") as "north" | "south" | "rome" | null;
        return adaptStandard({
          createInitial: () => E.createItalianUnificationInitialState(origin || undefined),
          getCurrentNode: E.getItalianUnificationCurrentNode,
          getAvailableChoices: E.getItalianUnificationAvailableChoices,
          applyChoice: E.applyItalianUnificationChoice,
          getAvailableNews: E.getItalianUnificationAvailableNews,
          getTurnNews: E.getItalianUnificationTurnNews,
          applyNewsEffects: E.applyItalianUnificationNewsEffects,
          save: E.saveItalianUnificationGame,
          load: E.loadItalianUnificationGame,
          getEndingTitle: E.getItalianUnificationEndingTitle,
          getEndingDescription: E.getItalianUnificationEndingDescription,
        });
      },
      actors: R.italianUnificationActors,
      mapCities: M.italianUnificationCities,
      allNews: N.italianUnificationNewsItems,
      entityConfig: EC.italianUnificationEntityConfig,
      MapComponent: MC.default,
      accentHsl: "145 50% 44%",
    };
  },

  "siege-of-constantinople": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/SiegeOfConstantinopleGameEngine"),
      import("@/data/siege-of-constantinople/news"),
      import("@/data/siege-of-constantinople/map"),
      import("@/data/siege-of-constantinople/relationships"),
      import("@/data/siege-of-constantinople/entity-config"),
      import("@/components/game/SiegeOfConstantinopleMap"),
    ]);
    return {
      id: "siege-of-constantinople",
      buildEngine: (params) => {
        const origin = params.get("origin") as "defender" | "attacker" | null;
        return adaptStandard({
          createInitial: () => E.createSiegeOfConstantinopleInitialState(origin || undefined),
          getCurrentNode: E.getSiegeOfConstantinopleCurrentNode,
          getAvailableChoices: E.getSiegeOfConstantinopleAvailableChoices,
          applyChoice: E.applySiegeOfConstantinopleChoice,
          getAvailableNews: E.getSiegeOfConstantinopleAvailableNews,
          getTurnNews: E.getSiegeOfConstantinopleTurnNews,
          applyNewsEffects: E.applySiegeOfConstantinopleNewsEffects,
          save: E.saveSiegeOfConstantinopleGame,
          load: E.loadSiegeOfConstantinopleGame,
          getEndingTitle: E.getSiegeOfConstantinopleEndingTitle,
          getEndingDescription: E.getSiegeOfConstantinopleEndingDescription,
        });
      },
      actors: R.siegeOfConstantinopleActors,
      mapCities: M.siegeOfConstantinopleCities,
      allNews: N.siegeOfConstantinopleNewsItems,
      entityConfig: EC.siegeOfConstantinopleEntityConfig,
      MapComponent: MC.default,
      accentHsl: "350 55% 50%",
    };
  },

  "carolingian-empire": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/CarolingianEmpireGameEngine"),
      import("@/data/carolingian-empire/news"),
      import("@/data/carolingian-empire/map"),
      import("@/data/carolingian-empire/relationships"),
      import("@/data/carolingian-empire/entity-config"),
      import("@/components/game/CarolingianEmpireMap"),
    ]);
    return {
      id: "carolingian-empire",
      buildEngine: (params) => {
        const origin = params.get("origin") as "frank" | "saxon" | null;
        return adaptStandard({
          createInitial: () => E.createCarolingianEmpireInitialState(origin || undefined),
          getCurrentNode: E.getCarolingianEmpireCurrentNode,
          getAvailableChoices: E.getCarolingianEmpireAvailableChoices,
          applyChoice: E.applyCarolingianEmpireChoice,
          getAvailableNews: E.getCarolingianEmpireAvailableNews,
          getTurnNews: E.getCarolingianEmpireTurnNews,
          applyNewsEffects: E.applyCarolingianEmpireNewsEffects,
          save: E.saveCarolingianEmpireGame,
          load: E.loadCarolingianEmpireGame,
          getEndingTitle: E.getCarolingianEmpireEndingTitle,
          getEndingDescription: E.getCarolingianEmpireEndingDescription,
        });
      },
      actors: R.carolingianEmpireActors,
      mapCities: M.carolingianEmpireCities,
      allNews: N.carolingianEmpireNewsItems,
      entityConfig: EC.carolingianEmpireEntityConfig,
      MapComponent: MC.default,
      accentHsl: "43 64% 45%",
    };
  },

  "magyar-raids": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/MagyarRaidsGameEngine"),
      import("@/data/magyar-raids/news"),
      import("@/data/magyar-raids/map"),
      import("@/data/magyar-raids/relationships"),
      import("@/data/magyar-raids/entity-config"),
      import("@/components/game/MagyarRaidsMap"),
    ]);
    return {
      id: "magyar-raids",
      buildEngine: (params) => {
        const origin = params.get("origin") as "magyar" | "german" | null;
        return adaptStandard({
          createInitial: () => E.createMagyarRaidsInitialState(origin || undefined),
          getCurrentNode: E.getMagyarRaidsCurrentNode,
          getAvailableChoices: E.getMagyarRaidsAvailableChoices,
          applyChoice: E.applyMagyarRaidsChoice,
          getAvailableNews: E.getMagyarRaidsAvailableNews,
          getTurnNews: E.getMagyarRaidsTurnNews,
          applyNewsEffects: E.applyMagyarRaidsNewsEffects,
          save: E.saveMagyarRaidsGame,
          load: E.loadMagyarRaidsGame,
          getEndingTitle: E.getMagyarRaidsEndingTitle,
          getEndingDescription: E.getMagyarRaidsEndingDescription,
        });
      },
      actors: R.magyarRaidsActors,
      mapCities: M.magyarRaidsCities,
      allNews: N.magyarRaidsNewsItems,
      entityConfig: EC.magyarRaidsEntityConfig,
      MapComponent: MC.default,
      accentHsl: "40 64% 45%",
    };
  },

  "black-death": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/BlackDeathGameEngine"),
      import("@/data/black-death/news"),
      import("@/data/black-death/map"),
      import("@/data/black-death/relationships"),
      import("@/data/black-death/entity-config"),
      import("@/components/game/BlackDeathMap"),
    ]);
    return {
      id: "black-death",
      buildEngine: (params) => {
        const origin = params.get("origin") as "merchant" | "physician" | "friar" | null;
        return adaptStandard({
          createInitial: () => E.createBlackDeathInitialState(origin || undefined),
          getCurrentNode: E.getBlackDeathCurrentNode,
          getAvailableChoices: E.getBlackDeathAvailableChoices,
          applyChoice: E.applyBlackDeathChoice,
          getAvailableNews: E.getBlackDeathAvailableNews,
          getTurnNews: E.getBlackDeathTurnNews,
          applyNewsEffects: E.applyBlackDeathNewsEffects,
          save: E.saveBlackDeathGame,
          load: E.loadBlackDeathGame,
          getEndingTitle: E.getBlackDeathEndingTitle,
          getEndingDescription: E.getBlackDeathEndingDescription,
        });
      },
      actors: R.blackDeathActors,
      mapCities: M.blackDeathCities,
      allNews: N.blackDeathNewsItems,
      entityConfig: EC.blackDeathEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 48% 42%",
    };
  },

  "napoleonic-wars": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/NapoleonicWarsGameEngine"),
      import("@/data/napoleonic-wars/news"),
      import("@/data/napoleonic-wars/map"),
      import("@/data/napoleonic-wars/relationships"),
      import("@/data/napoleonic-wars/entity-config"),
      import("@/components/game/NapoleonicWarsMap"),
    ]);
    return {
      id: "napoleonic-wars",
      buildEngine: (params) => {
        const origin = params.get("origin") as "grognard" | "prussian" | "bluejacket" | null;
        return adaptStandard({
          createInitial: () => E.createNapoleonicWarsInitialState(origin || undefined),
          getCurrentNode: E.getNapoleonicWarsCurrentNode,
          getAvailableChoices: E.getNapoleonicWarsAvailableChoices,
          applyChoice: E.applyNapoleonicWarsChoice,
          getAvailableNews: E.getNapoleonicWarsAvailableNews,
          getTurnNews: E.getNapoleonicWarsTurnNews,
          applyNewsEffects: E.applyNapoleonicWarsNewsEffects,
          save: E.saveNapoleonicWarsGame,
          load: E.loadNapoleonicWarsGame,
          getEndingTitle: E.getNapoleonicWarsEndingTitle,
          getEndingDescription: E.getNapoleonicWarsEndingDescription,
        });
      },
      actors: R.napoleonicWarsActors,
      mapCities: M.napoleonicWarsCities,
      allNews: N.napoleonicWarsNewsItems,
      entityConfig: EC.napoleonicWarsEntityConfig,
      MapComponent: MC.default,
      accentHsl: "40 66% 52%", // imperial gold, faction-neutral across the three vantages
    };
  },

  "world-war-two-europe": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/WorldWarTwoEuropeGameEngine"),
      import("@/data/world-war-two-europe/news"),
      import("@/data/world-war-two-europe/map"),
      import("@/data/world-war-two-europe/relationships"),
      import("@/data/world-war-two-europe/entity-config"),
      import("@/components/game/WorldWarTwoEuropeMap"),
    ]);
    return {
      id: "world-war-two-europe",
      buildEngine: (params) => {
        const origin = params.get("origin") as "witness" | "occupe" | "blitz" | "stiller_held" | null;
        return adaptStandard({
          createInitial: () => E.createWorldWarTwoEuropeInitialState(origin || undefined),
          getCurrentNode: E.getWorldWarTwoEuropeCurrentNode,
          getAvailableChoices: E.getWorldWarTwoEuropeAvailableChoices,
          applyChoice: E.applyWorldWarTwoEuropeChoice,
          getAvailableNews: E.getWorldWarTwoEuropeAvailableNews,
          getTurnNews: E.getWorldWarTwoEuropeTurnNews,
          applyNewsEffects: E.applyWorldWarTwoEuropeNewsEffects,
          save: E.saveWorldWarTwoEuropeGame,
          load: E.loadWorldWarTwoEuropeGame,
          getEndingTitle: E.getWorldWarTwoEuropeEndingTitle,
          getEndingDescription: E.getWorldWarTwoEuropeEndingDescription,
        });
      },
      actors: R.worldWarTwoEuropeActors,
      mapCities: M.worldWarTwoEuropeCities,
      allNews: N.worldWarTwoEuropeNewsItems,
      entityConfig: EC.worldWarTwoEuropeEntityConfig,
      MapComponent: MC.default,
      accentHsl: "210 12% 60%", // ashen grey — no faction gold; the war carries no glory
    };
  },

  "thirty-years-war": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ThirtyYearsWarGameEngine"),
      import("@/data/thirty-years-war/news"),
      import("@/data/thirty-years-war/map"),
      import("@/data/thirty-years-war/relationships"),
      import("@/data/thirty-years-war/entity-config"),
      import("@/components/game/ThirtyYearsWarMap"),
    ]);
    return {
      id: "thirty-years-war",
      buildEngine: (params) => {
        const origin = params.get("origin") as "commoner" | "soldier" | null;
        return adaptStandard({
          createInitial: () => E.createThirtyYearsWarInitialState(origin || undefined),
          getCurrentNode: E.getThirtyYearsWarCurrentNode,
          getAvailableChoices: E.getThirtyYearsWarAvailableChoices,
          applyChoice: E.applyThirtyYearsWarChoice,
          getAvailableNews: E.getThirtyYearsWarAvailableNews,
          getTurnNews: E.getThirtyYearsWarTurnNews,
          applyNewsEffects: E.applyThirtyYearsWarNewsEffects,
          save: E.saveThirtyYearsWarGame,
          load: E.loadThirtyYearsWarGame,
          getEndingTitle: E.getThirtyYearsWarEndingTitle,
          getEndingDescription: E.getThirtyYearsWarEndingDescription,
        });
      },
      actors: R.thirtyYearsWarActors,
      mapCities: M.thirtyYearsWarCities,
      allNews: N.thirtyYearsWarNewsItems,
      entityConfig: EC.thirtyYearsWarEntityConfig,
      MapComponent: MC.default,
      // Faction-neutral ash/ember — both the Protestant and Imperial roads
      // share the same burned-country map; distinct from the gold accents of
      // the neighbouring Carolingian (43) and Magyar (40) entries.
      accentHsl: "20 25% 42%",
    };
  },

  "hussite-wars": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/HussiteWarsGameEngine"),
      import("@/data/hussite-wars/news"),
      import("@/data/hussite-wars/map"),
      import("@/data/hussite-wars/relationships"),
      import("@/data/hussite-wars/entity-config"),
      import("@/components/game/HussiteWarsMap"),
    ]);
    return {
      id: "hussite-wars",
      buildEngine: () => {
        return adaptStandard({
          createInitial: () => E.createHussiteWarsInitialState(),
          getCurrentNode: E.getHussiteWarsCurrentNode,
          getAvailableChoices: E.getHussiteWarsAvailableChoices,
          applyChoice: E.applyHussiteWarsChoice,
          getAvailableNews: E.getHussiteWarsAvailableNews,
          getTurnNews: E.getHussiteWarsTurnNews,
          applyNewsEffects: E.applyHussiteWarsNewsEffects,
          save: E.saveHussiteWarsGame,
          load: E.loadHussiteWarsGame,
          getEndingTitle: E.getHussiteWarsEndingTitle,
          getEndingDescription: E.getHussiteWarsEndingDescription,
        });
      },
      actors: R.hussiteWarsActors,
      mapCities: M.hussiteWarsCities,
      allNews: N.hussiteWarsNewsItems,
      entityConfig: EC.hussiteWarsEntityConfig,
      MapComponent: MC.default,
      // The Hussite crimson — the chalice raised on a red field; distinct from
      // the ash/ember of the neighbouring Thirty Years' War (hre) entry.
      accentHsl: "355 55% 40%",
    };
  },

  "investiture-controversy": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/InvestitureControversyGameEngine"),
      import("@/data/investiture-controversy/news"),
      import("@/data/investiture-controversy/map"),
      import("@/data/investiture-controversy/relationships"),
      import("@/data/investiture-controversy/entity-config"),
      import("@/components/game/InvestitureControversyMap"),
    ]);
    return {
      id: "investiture-controversy",
      buildEngine: () => {
        return adaptStandard({
          createInitial: () => E.createInvestitureControversyInitialState(),
          getCurrentNode: E.getInvestitureControversyCurrentNode,
          getAvailableChoices: E.getInvestitureControversyAvailableChoices,
          applyChoice: E.applyInvestitureControversyChoice,
          getAvailableNews: E.getInvestitureControversyAvailableNews,
          getTurnNews: E.getInvestitureControversyTurnNews,
          applyNewsEffects: E.applyInvestitureControversyNewsEffects,
          save: E.saveInvestitureControversyGame,
          load: E.loadInvestitureControversyGame,
          getEndingTitle: E.getInvestitureControversyEndingTitle,
          getEndingDescription: E.getInvestitureControversyEndingDescription,
        });
      },
      actors: R.investitureControversyActors,
      mapCities: M.investitureControversyCities,
      allNews: N.investitureControversyNewsItems,
      entityConfig: EC.investitureControversyEntityConfig,
      MapComponent: MC.default,
      // Imperial Salian violet-grey — the Emperor-and-Pope quarrel; distinct
      // from the Hussite crimson and the Thirty Years' War ash of the hre arc.
      accentHsl: "280 22% 42%",
    };
  },

  "german-unification": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/GermanUnificationGameEngine"),
      import("@/data/german-unification/news"),
      import("@/data/german-unification/map"),
      import("@/data/german-unification/relationships"),
      import("@/data/german-unification/entity-config"),
      import("@/components/game/GermanUnificationMap"),
    ]);
    return {
      id: "german-unification",
      buildEngine: () => {
        return adaptStandard({
          createInitial: () => E.createGermanUnificationInitialState(),
          getCurrentNode: E.getGermanUnificationCurrentNode,
          getAvailableChoices: E.getGermanUnificationAvailableChoices,
          applyChoice: E.applyGermanUnificationChoice,
          getAvailableNews: E.getGermanUnificationAvailableNews,
          getTurnNews: E.getGermanUnificationTurnNews,
          applyNewsEffects: E.applyGermanUnificationNewsEffects,
          save: E.saveGermanUnificationGame,
          load: E.loadGermanUnificationGame,
          getEndingTitle: E.getGermanUnificationEndingTitle,
          getEndingDescription: E.getGermanUnificationEndingDescription,
        });
      },
      actors: R.germanUnificationActors,
      mapCities: M.germanUnificationCities,
      allNews: N.germanUnificationNewsItems,
      entityConfig: EC.germanUnificationEntityConfig,
      MapComponent: MC.default,
      // Prussian steel-blue — iron and blood; distinct from the other hre
      // accents (Hussite crimson, Investiture violet-grey, the TYW ash).
      accentHsl: "212 44% 56%",
    };
  },

  "protestant-reformation": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/ProtestantReformationGameEngine"),
      import("@/data/protestant-reformation/news"),
      import("@/data/protestant-reformation/map"),
      import("@/data/protestant-reformation/relationships"),
      import("@/data/protestant-reformation/entity-config"),
      import("@/components/game/ProtestantReformationMap"),
    ]);
    return {
      id: "protestant-reformation",
      buildEngine: (params) => {
        const origin = params.get("origin") as "reformer" | "roman" | null;
        return adaptStandard({
          createInitial: () => E.createProtestantReformationInitialState(origin || undefined),
          getCurrentNode: E.getProtestantReformationCurrentNode,
          getAvailableChoices: E.getProtestantReformationAvailableChoices,
          applyChoice: E.applyProtestantReformationChoice,
          getAvailableNews: E.getProtestantReformationAvailableNews,
          getTurnNews: E.getProtestantReformationTurnNews,
          applyNewsEffects: E.applyProtestantReformationNewsEffects,
          save: E.saveProtestantReformationGame,
          load: E.loadProtestantReformationGame,
          getEndingTitle: E.getProtestantReformationEndingTitle,
          getEndingDescription: E.getProtestantReformationEndingDescription,
        });
      },
      actors: R.protestantReformationActors,
      mapCities: M.protestantReformationCities,
      allNews: N.protestantReformationNewsItems,
      entityConfig: EC.protestantReformationEntityConfig,
      MapComponent: MC.default,
      // Reformation ink-black-and-crimson — the black of printed type, the
      // crimson of the stake and the cardinal alike.
      accentHsl: "352 60% 45%",
    };
  },

  "greek-independence": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/GreekIndependenceGameEngine"),
      import("@/data/greek-independence/news"),
      import("@/data/greek-independence/map"),
      import("@/data/greek-independence/relationships"),
      import("@/data/greek-independence/entity-config"),
      import("@/components/game/GreekIndependenceMap"),
    ]);
    return {
      id: "greek-independence",
      buildEngine: (params) => {
        const origin = params.get("origin") as "land" | "sea" | null;
        return adaptStandard({
          createInitial: () => E.createGreekIndependenceInitialState(origin || undefined),
          getCurrentNode: E.getGreekIndependenceCurrentNode,
          getAvailableChoices: E.getGreekIndependenceAvailableChoices,
          applyChoice: E.applyGreekIndependenceChoice,
          getAvailableNews: E.getGreekIndependenceAvailableNews,
          getTurnNews: E.getGreekIndependenceTurnNews,
          applyNewsEffects: E.applyGreekIndependenceNewsEffects,
          save: E.saveGreekIndependenceGame,
          load: E.loadGreekIndependenceGame,
          getEndingTitle: E.getGreekIndependenceEndingTitle,
          getEndingDescription: E.getGreekIndependenceEndingDescription,
        });
      },
      actors: R.greekIndependenceActors,
      mapCities: M.greekIndependenceCities,
      allNews: N.greekIndependenceNewsItems,
      entityConfig: EC.greekIndependenceEntityConfig,
      MapComponent: MC.default,
      accentHsl: "210 60% 45%",
    };
  },

  "serbian-uprisings": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/SerbianUprisingsGameEngine"),
      import("@/data/serbian-uprisings/news"),
      import("@/data/serbian-uprisings/map"),
      import("@/data/serbian-uprisings/relationships"),
      import("@/data/serbian-uprisings/entity-config"),
      import("@/components/game/SerbianUprisingsMap"),
    ]);
    return {
      id: "serbian-uprisings",
      buildEngine: () =>
        adaptStandard({
          createInitial: () => E.createSerbianUprisingsInitialState(),
          getCurrentNode: E.getSerbianUprisingsCurrentNode,
          getAvailableChoices: E.getSerbianUprisingsAvailableChoices,
          applyChoice: E.applySerbianUprisingsChoice,
          getAvailableNews: E.getSerbianUprisingsAvailableNews,
          getTurnNews: E.getSerbianUprisingsTurnNews,
          applyNewsEffects: E.applySerbianUprisingsNewsEffects,
          save: E.saveSerbianUprisingsGame,
          load: E.loadSerbianUprisingsGame,
          getEndingTitle: E.getSerbianUprisingsEndingTitle,
          getEndingDescription: E.getSerbianUprisingsEndingDescription,
        }),
      actors: R.serbianUprisingsActors,
      mapCities: M.serbianUprisingsCities,
      allNews: N.serbianUprisingsNewsItems,
      entityConfig: EC.serbianUprisingsEntityConfig,
      MapComponent: MC.default,
      accentHsl: "0 60% 50%",
    };
  },
  "ottoman-expansion-europe": async () => {
    const [E, N, M, R, EC, MC] = await Promise.all([
      import("@/engine/OttomanExpansionGameEngine"),
      import("@/data/ottoman-expansion-europe/news"),
      import("@/data/ottoman-expansion-europe/map"),
      import("@/data/ottoman-expansion-europe/relationships"),
      import("@/data/ottoman-expansion-europe/entity-config"),
      import("@/components/game/OttomanExpansionMap"),
    ]);
    return {
      id: "ottoman-expansion-europe",
      buildEngine: (params) => {
        const origin = params.get("origin") as "defender" | "attacker" | null;
        return adaptStandard({
          createInitial: () => E.createOttomanExpansionInitialState(origin || undefined),
          getCurrentNode: E.getOttomanExpansionCurrentNode,
          getAvailableChoices: E.getOttomanExpansionAvailableChoices,
          applyChoice: E.applyOttomanExpansionChoice,
          getAvailableNews: E.getOttomanExpansionAvailableNews,
          getTurnNews: E.getOttomanExpansionTurnNews,
          applyNewsEffects: E.applyOttomanExpansionNewsEffects,
          save: E.saveOttomanExpansionGame,
          load: E.loadOttomanExpansionGame,
          getEndingTitle: E.getOttomanExpansionEndingTitle,
          getEndingDescription: E.getOttomanExpansionEndingDescription,
        });
      },
      actors: R.ottomanExpansionActors,
      mapCities: M.ottomanExpansionCities,
      allNews: N.ottomanExpansionNewsItems,
      entityConfig: EC.ottomanExpansionEntityConfig,
      MapComponent: MC.default,
      accentHsl: "43 64% 48%",
    };
  },
};

// ── Lighter cities-only loaders ───────────────────────────────────────
//
// ProtagonistIntro's resume-prompt preview needs only the chronicle's
// cities array, not the full engine + map component. Vite dedupes the
// underlying `@/data/X/map` chunk between this loader and the full
// `entryLoaders[X]` above — both yield the same chunk reference at
// runtime, so loading cities first and then the full entry doesn't
// double-fetch.

const cityLoaders: Record<string, () => Promise<City[]>> = {
  "eighty-years-war":      () => import("@/data/eighty-years-war/map").then((m) => m.cities),
  "dutch-golden-age-voc":  () => import("@/data/dutch-golden-age/map").then((m) => m.vocCities),
  "burgundian-netherlands":() => import("@/data/burgundian-netherlands/map").then((m) => m.burgundianCities),
  "belgian-revolution":    () => import("@/data/belgian-revolution/map").then((m) => m.belgianRevolutionCities),
  "christianization-scandinavia": () => import("@/data/christianization-scandinavia/map").then((m) => m.christianizationScandinaviaCities),
  "settlement-of-iceland": () => import("@/data/settlement-of-iceland/map").then((m) => m.settlementOfIcelandCities),
  "kalmar-union":          () => import("@/data/kalmar-union/map").then((m) => m.kalmarUnionCities),
  "great-northern-war":    () => import("@/data/great-northern-war/map").then((m) => m.greatNorthernWarCities),
  "livonian-crusade":     () => import("@/data/livonian-crusade/map").then((m) => m.livonianCrusadeCities),
  "mongol-sack-of-rus":   () => import("@/data/mongol-sack-of-rus/map").then((m) => m.mongolInvasionsCities),
  "the-deluge":           () => import("@/data/the-deluge/map").then((m) => m.delugeCities),
  "reconquista-expansion": () => import("@/data/reconquista/map").then((m) => m.reconquistaCities),
  "fall-of-granada":       () => import("@/data/fall-of-granada/map").then((m) => m.fallOfGranadaCities),
  "first-voyages-west":    () => import("@/data/first-voyages-west/map").then((m) => m.firstVoyagesWestCities),
  "iberian-union":         () => import("@/data/iberian-union/map").then((m) => m.iberianUnionCities),
  "peninsular-war":        () => import("@/data/peninsular-war/map").then((m) => m.peninsularWarCities),
  "spanish-civil-war":     () => import("@/data/spanish-civil-war/map").then((m) => m.spanishCivilWarCities),
  "hundred-years-war":     () => import("@/data/hundred-years-war/map").then((m) => m.hundredYearsWarCities),
  "french-revolution":     () => import("@/data/french-revolution/map").then((m) => m.frenchRevolutionCities),
  "second-republic":       () => import("@/data/second-republic/map").then((m) => m.secondRepublicCities),
  "albigensian-crusade":   () => import("@/data/albigensian-crusade/map").then((m) => m.albigensianCrusadeCities),
  "viking-dublin":         () => import("@/data/viking-dublin/map").then((m) => m.vikingDublinCities),
  "norman-conquest":       () => import("@/data/norman-conquest/map").then((m) => m.normanConquestCities),
  "the-anarchy":           () => import("@/data/the-anarchy/map").then((m) => m.anarchyCities),
  "wars-of-the-roses":     () => import("@/data/wars-of-the-roses/map").then((m) => m.worsOfTheRosesCities),
  "english-civil-wars":    () => import("@/data/english-civil-wars/map").then((m) => m.ecwCities),
  "irish-independence":    () => import("@/data/irish-independence/map").then((m) => m.iwoiCities),
  "russian-revolution":    () => import("@/data/russian-revolution/map").then((m) => m.russianRevolutionCities),
  "world-war-one":         () => import("@/data/world-war-one/map").then((m) => m.worldWarICities),
  "hungarian-revolution-1956": () => import("@/data/hungarian-revolution-1956/map").then((m) => m.hungarianRevolution1956Cities),
  "hungarian-revolution-1848": () => import("@/data/hungarian-revolution-1848/map").then((m) => m.hungarianRevolution1848Cities),
  "prague-spring-1968":        () => import("@/data/prague-spring-1968/map").then((m) => m.pragueSpring1968Cities),
  "two-germanies":             () => import("@/data/two-germanies/map").then((m) => m.twoGermaniesCities),
  "fall-of-communism":         () => import("@/data/fall-of-communism/map").then((m) => m.fallOfCommunismCities),
  "italian-city-states":       () => import("@/data/italian-city-states/map").then((m) => m.italianCityStatesCities),
  "medici-florence":           () => import("@/data/medici-florence/map").then((m) => m.mediciFlorenceCities),
  "italian-wars":              () => import("@/data/italian-wars/map").then((m) => m.italianWarsCities),
  "italian-unification":       () => import("@/data/italian-unification/map").then((m) => m.italianUnificationCities),
  "siege-of-constantinople":   () => import("@/data/siege-of-constantinople/map").then((m) => m.siegeOfConstantinopleCities),
  "serbian-uprisings":         () => import("@/data/serbian-uprisings/map").then((m) => m.serbianUprisingsCities),
  "carolingian-empire":        () => import("@/data/carolingian-empire/map").then((m) => m.carolingianEmpireCities),
  "magyar-raids":              () => import("@/data/magyar-raids/map").then((m) => m.magyarRaidsCities),
  "black-death":               () => import("@/data/black-death/map").then((m) => m.blackDeathCities),
  "napoleonic-wars":           () => import("@/data/napoleonic-wars/map").then((m) => m.napoleonicWarsCities),
  "world-war-two-europe":      () => import("@/data/world-war-two-europe/map").then((m) => m.worldWarTwoEuropeCities),
  "thirty-years-war":          () => import("@/data/thirty-years-war/map").then((m) => m.thirtyYearsWarCities),
  "hussite-wars":              () => import("@/data/hussite-wars/map").then((m) => m.hussiteWarsCities),
  "investiture-controversy":   () => import("@/data/investiture-controversy/map").then((m) => m.investitureControversyCities),
  "german-unification":        () => import("@/data/german-unification/map").then((m) => m.germanUnificationCities),
  "protestant-reformation":    () => import("@/data/protestant-reformation/map").then((m) => m.protestantReformationCities),
  "greek-independence":        () => import("@/data/greek-independence/map").then((m) => m.greekIndependenceCities),
  "ottoman-expansion-europe":  () => import("@/data/ottoman-expansion-europe/map").then((m) => m.ottomanExpansionCities),
};

// ── Cache + accessors ─────────────────────────────────────────────────

const entryCache = new Map<string, ChronicleRegistryEntry>();
const entryInflight = new Map<string, Promise<ChronicleRegistryEntry>>();

/** Async loader. First call kicks off the dynamic imports; subsequent
 *  calls (after resolution) return the cached entry instantly. Concurrent
 *  callers share a single in-flight promise. Unknown ids fall back to
 *  Eighty Years' War (the original) — preserves the pre-refactor
 *  fallback behaviour. */
export async function loadChronicle(id: string): Promise<ChronicleRegistryEntry> {
  if (entryCache.has(id)) return entryCache.get(id)!;
  if (entryInflight.has(id)) return entryInflight.get(id)!;
  const loaderId = entryLoaders[id] ? id : "eighty-years-war";
  const promise = entryLoaders[loaderId]().then((entry) => {
    entryCache.set(id, entry);
    entryInflight.delete(id);
    return entry;
  });
  entryInflight.set(id, promise);
  return promise;
}

/** React-Suspense-compatible accessor. Returns the entry synchronously
 *  if cached, else throws the loading promise. The nearest `<Suspense>`
 *  boundary catches the throw and renders its fallback until the promise
 *  resolves; React then re-renders with the cached entry. */
export function useChronicleEntry(id: string): ChronicleRegistryEntry {
  const cached = entryCache.get(id);
  if (cached) return cached;
  // Throwing a Promise is the React Suspense protocol.
  throw loadChronicle(id);
}

/** Synchronous accessor. Returns the cached entry; throws if the id
 *  hasn't been loaded yet. Tests pre-populate the cache via
 *  `beforeAll(() => Promise.all(IDS.map(loadChronicle)))` and then call
 *  this freely. Production callers should prefer `useChronicleEntry`
 *  inside React or `loadChronicle` elsewhere. */
export function getChronicle(id: string): ChronicleRegistryEntry {
  const entry = entryCache.get(id) ?? entryCache.get("eighty-years-war");
  if (!entry) {
    throw new Error(
      `getChronicle("${id}") called before the entry was loaded. ` +
      `Use useChronicleEntry(id) in React components, loadChronicle(id) ` +
      `elsewhere, or pre-populate the cache from a test setup hook.`,
    );
  }
  return entry;
}

/** Lighter accessor for surfaces that need only the cities array
 *  (ProtagonistIntro's resume preview). Skips the engine + map
 *  component, so the bundle stays small. */
export async function loadChronicleCities(id: string | undefined): Promise<City[]> {
  if (!id) return [];
  const loader = cityLoaders[id] ?? cityLoaders["eighty-years-war"];
  return loader();
}

/** All chronicle ids with a registry entry. Eager-safe — no chronicle
 *  data is touched, only the keys of `entryLoaders`. */
export const CHRONICLE_IDS = Object.keys(entryLoaders);
